import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
	convertFromUTC,
	fixStartEndIntervals,
	calculateWorkTime,
	formatSecondsToTime,
} from "utils/datetime";
import { getContrastColor } from "utils/utils";

const getWorkIntervals = (assignment) => {
	const raw = assignment.time_intervals_from_manual
		? assignment.time_intervals
		: assignment.shift?.time_intervals;

	if (!Array.isArray(raw)) return [];

	const workOnly = raw.filter(
		(ti) => !ti?.time_type || ti.time_type.work_time === true
	);

	return fixStartEndIntervals(workOnly.length > 0 ? workOnly : raw);
};

const getTimeRange = (assignment) => {
	const intervals = getWorkIntervals(assignment);
	if (!intervals || intervals.length === 0) return { range: "", total: "" };

	const start = convertFromUTC(intervals[0].start);
	const end = convertFromUTC(intervals[intervals.length - 1].end);
	const range = start && end ? `${start}-${end}` : "";

	const totalSeconds = calculateWorkTime(intervals);
	const total = formatSecondsToTime(totalSeconds, "hm");

	return { range, total };
};

const ShiftItem = ({ assignment }) => {
	const shift = assignment.shift;
	if (!shift) return null;

	const { range, total } = getTimeRange(assignment);
	const color = shift.background_color || "#5b49fe";
	const func = assignment.function;
	const ubications = assignment.ubications || [];
	const ubication = ubications[0] || null;

	return (
		<div className="mobile-calendar-day-card">
			<div
				className="mobile-calendar-day-card__bar"
				style={{ backgroundColor: color }}
			/>
			<div className="mobile-calendar-day-card__body">
				<div className="mobile-calendar-day-card__row">
					<span className="mobile-calendar-day-card__name">
						{shift.name}
					</span>
					{total && (
						<span className="mobile-calendar-day-card__total">
							{total}
						</span>
					)}
				</div>
				{range && (
					<div className="mobile-calendar-day-card__range">{range}</div>
				)}
				<div className="mobile-calendar-day-card__tags">
					{func && (
						<span
							className="mobile-calendar-day-card__tag"
							style={{
								backgroundColor:
									func.background_color || "#ebe9ff",
								color: func.background_color
									? getContrastColor(func.background_color)
									: undefined,
							}}
						>
							{func.name}
						</span>
					)}
					{ubications.length > 1 ? (
						<span className="mobile-calendar-day-card__ubication-list">
							{ubications.map((u, i) => (
								<span
									key={`${u.id}-${i}`}
									className="mobile-calendar-day-card__ubication"
								>
									{u.name}
								</span>
							))}
						</span>
					) : (
						ubication && (
							<span className="mobile-calendar-day-card__ubication">
								{ubication.name}
							</span>
						)
					)}
				</div>
			</div>
		</div>
	);
};

const formatDayLabel = (day, t) => {
	const d = moment(day, "YYYY-MM-DD");
	const raw = d.format(t("calendar.dayLabelFormat"));
	return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const MobileCalendarDayList = ({ day, dayData, plan }) => {
	const { t } = useTranslation();
	const assignments = plan?.assignments || [];
	const isHoliday = !!dayData?.day_type?.holiday;
	const isWorkedHoliday = !!dayData?.worked_holiday;
	const holidayName = dayData?.day_type?.name;
	const holidayColor = dayData?.day_type?.color;
	const holidayDesc = dayData?.comments || dayData?.day_type?.description;
	const isAbsence = !!dayData?.absence;
	const absenceName = dayData?.absence?.absence_type?.name;

	const hasContent =
		assignments.length > 0 || isHoliday || isWorkedHoliday || isAbsence;

	return (
		<div className="mobile-calendar-day-list">
			<div className="mobile-calendar-day-list__header">
				{formatDayLabel(day, t)}
			</div>

			{!hasContent && (
				<div className="mobile-calendar-day-list__empty">
					{t("calendar.noEventsThisDay")}
				</div>
			)}

			{(isHoliday || isWorkedHoliday) && (
				<div
					className="mobile-calendar-day-list__holiday"
					style={
						holidayColor
							? {
									backgroundColor: holidayColor,
									color: getContrastColor(holidayColor),
								}
							: undefined
					}
				>
					<div className="mobile-calendar-day-list__holiday-title">
						{isWorkedHoliday ? t("calendar.workedHoliday") : t("calendar.holiday")}
					</div>
					{holidayName && (
						<div className="mobile-calendar-day-list__holiday-name">
							{holidayName}
						</div>
					)}
					{holidayDesc && (
						<div className="mobile-calendar-day-list__holiday-desc">
							{holidayDesc}
						</div>
					)}
				</div>
			)}

			{isAbsence && (
				<div className="mobile-calendar-day-list__absence">
					<span className="mobile-calendar-day-list__absence-dot" />
					<span>
						{t("calendar.absence")}{absenceName ? ` · ${absenceName}` : ""}
					</span>
				</div>
			)}

			{assignments.length > 0 && (
				<div className="mobile-calendar-day-list__shifts">
					{assignments.map((a, i) => (
						<ShiftItem key={`${a.id}-${i}`} assignment={a} />
					))}
				</div>
			)}
		</div>
	);
};

export default MobileCalendarDayList;
